import React, { useState, useEffect, useRef } from 'react';
import { Search, Package, Loader2 } from 'lucide-react';
import { ItemsDto } from '../../../../types/items';
import { itemsService } from '../../../../services/itemsService';
import { getItemTypeLabel } from '../../../../types/itemTypeChange';

interface Step1Props {
  onItemSelected: (item: ItemsDto) => void;
  /** Hvis satt, vises kun varer med ItemType i listen. Tom liste = ingen treff. */
  itemTypeFilter?: number[];
  /** Tittel og undertittel kan overstyres. */
  title?: string;
  subtitle?: string;
}

export const Step1SelectItem: React.FC<Step1Props> = ({
  onItemSelected,
  itemTypeFilter,
  title,
  subtitle,
}) => {
  const [term, setTerm] = useState('');
  const [items, setItems] = useState<ItemsDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (term.trim().length < 2) {
      setItems([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await itemsService.searchCurrentItems(term.trim());
        const filtered = itemTypeFilter && itemTypeFilter.length > 0
          ? data.filter((i) => itemTypeFilter.includes(i.itemType ?? 0))
          : data;
        setItems(filtered);
      } catch (e: any) {
        setError(e?.message || 'Søk feilet');
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [term, itemTypeFilter]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">{title ?? 'Steg 1: Velg vare'}</h2>
        <p className="text-sm text-gray-600 mb-4">
          {subtitle ?? 'Søk etter varen du ønsker å endre varetype for. Minst 2 tegn.'}
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Søk: varenummer, beskrivelse, strekkode, leverandør-varenummer"
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
          autoFocus
        />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-red-500" />
        </div>
      )}

      {error && <div className="text-sm text-red-600 py-2">{error}</div>}

      {!loading && items.length > 0 && (
        <div className="border border-gray-200 rounded-md overflow-hidden max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Varenr.</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Beskrivelse</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Varetype</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Beh.</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr key={`${item.companyNo}-${item.itemNo}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">{item.itemNo}</td>
                  <td className="px-3 py-2">{item.description1 || '-'}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      {getItemTypeLabel(item.itemType)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">{item.quantity ?? 0}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => onItemSelected(item)}
                      className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      <Package className="h-3 w-3" />
                      Velg
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && term.trim().length >= 2 && items.length === 0 && !error && (
        <p className="text-sm text-gray-500 text-center py-6">Ingen varer funnet.</p>
      )}
    </div>
  );
};
